/**
 * PDFControls Component
 * Navigation and zoom controls for the PDF viewer.
 */
import React, { useState } from 'react';

interface PDFControlsProps {
  pageNumber: number;
  numPages: number;
  scale: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onGoToPage: (page: number) => void;
  /** Hide page navigation buttons (for scrollable view) */
  showPageNav?: boolean;
}

const PDFControls: React.FC<PDFControlsProps> = ({
  pageNumber,
  numPages,
  scale,
  onPrevPage,
  onNextPage,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onGoToPage,
  showPageNav = true,
}) => {
  const [inputPage, setInputPage] = useState<string>(String(pageNumber));

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputPage(e.target.value);
  };

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const page = parseInt(inputPage, 10);
      if (!isNaN(page) && page >= 1 && page <= numPages) {
        onGoToPage(page);
      } else {
        setInputPage(String(pageNumber));
      }
    }
  };

  const handlePageInputBlur = () => {
    setInputPage(String(pageNumber));
  };

  // Update input when page changes externally
  React.useEffect(() => {
    setInputPage(String(pageNumber));
  }, [pageNumber]);

  return (
    <div className="flex items-center justify-between p-3 bg-white border-b border-gray-200 shadow-sm">
      {/* Page Navigation / Jump to page */}
      <div className="flex items-center space-x-2">
        {showPageNav && (
          <button
            onClick={onPrevPage}
            disabled={pageNumber <= 1}
            aria-label="Previous page"
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
        )}

        <div className="flex items-center space-x-1">
          <span className="text-sm text-gray-500">Go to page:</span>
          <input
            type="text"
            value={inputPage}
            onChange={handlePageInputChange}
            onKeyDown={handlePageInputKeyDown}
            onBlur={handlePageInputBlur}
            className="w-12 px-2 py-1 text-center text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Page number"
          />
          <span className="text-sm text-gray-500">of {numPages}</span>
        </div>

        {showPageNav && (
          <button
            onClick={onNextPage}
            disabled={pageNumber >= numPages}
            aria-label="Next page"
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        )}
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center space-x-2">
        <button
          onClick={onZoomOut}
          disabled={scale <= 0.5}
          aria-label="Zoom out"
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          -
        </button>

        <button
          onClick={onResetZoom}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          aria-label="Reset zoom"
        >
          {Math.round(scale * 100)}%
        </button>

        <button
          onClick={onZoomIn}
          disabled={scale >= 3.0}
          aria-label="Zoom in"
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          +
        </button>
      </div>
    </div>
  );
};

export default PDFControls;
